import { observer } from 'mobx-react-lite'
import { Link } from 'react-router-dom'
import { Button, Message } from 'semantic-ui-react'
import './ResetPassword.css'
import FWOTextInput from '../form/FWOTextInput'
import * as Yup from 'yup'
import { Form, Formik, FormikHelpers } from 'formik'
import ProfileService from '../../services/ProfileService'
import React, { useState, Suspense } from 'react'
import Conditional from '../common/Conditional'
import { PATHS } from '../Routes'
import Loader from '../common/Loader'
import Protected from '../Protected'
import { UserPassword } from '../../models/Password'
import useErrorHandler from '../../hooks/useErrorHandler'

const ResetPassword = observer(() => {
  const [, , handleErrors, , clearErrors] = useErrorHandler()
  const validateSchema = Yup.object({
    password: Yup.string()
      .required('Password is required')
      .matches(
        /^((?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])|(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%&\/=?_.,:;\\-])|(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%&\/=?_.,:;\\-])|(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%&\/=?_.,:;\\-])).{10,}$/,
        'Enter a valid password.'
      )
      .min(14, 'Password must be between 14 and 32 characters.')
      .max(32, 'Password must be between 14 and 32 characters.'),
    comparePassword: Yup.string()
      .required('Please re-enter password')
      .oneOf([Yup.ref('password'), null], 'Passwords must match'),
  })
  const [isSaved, setSaved] = useState(false)
  const { resetPassword: resetPassword } = ProfileService

  function handleFormSubmit(
    values: UserPassword,
    actions: FormikHelpers<UserPassword>
  ) {
    setSaved(false)
    resetPassword({
      currentPassword: values.currentPassword,
      password: values.password,
    })
      .then(() => {
        clearErrors()
        setSaved(true)
        actions.setSubmitting(false)
      })
      .catch((error) => {
        handleErrors(error)
      })
      .finally(() => {
        window.scrollTo(0, 0)
      })
  }

  return (
    <Protected>
      <Suspense fallback={<Loader />}>
        <nav className="breadcrumbs dnd-s-mob">
          <ol>
            <li>
              <Link tabIndex={0} to={PATHS.Profile}>
                Back to previous page
              </Link>
            </li>
          </ol>
        </nav>
        <div className="wrapper">
          <section className="content">
            <div className="columns">
              <main>
                <Formik
                  validationSchema={validateSchema}
                  enableReinitialize
                  initialValues={new UserPassword()}
                  onSubmit={(values, actions) =>
                    handleFormSubmit(values, actions)
                  }
                >
                  {({ isValid, isSubmitting, dirty }) => (
                    <Form
                      className="ui form osp-variant large"
                      autoComplete="off"
                    >
                      <h1>Change Password</h1>
                      <Conditional if={isValid && !isSubmitting && isSaved}>
                        <Message positive>
                          <Message.Header>
                            <h4>Success</h4>
                            <div>
                              You have successfully updated your password
                            </div>
                          </Message.Header>
                        </Message>
                      </Conditional>
                      <FWOTextInput
                        type="password"
                        placeholder="Current password"
                        label="Current password"
                        ariaLabel="Current password"
                        name="currentPassword"
                        required={true}
                        aria-label="Current password"
                      />
                      <FWOTextInput
                        type="password"
                        placeholder="New password"
                        label="New password"
                        ariaLabel="New password"
                        name="password"
                        required={true}
                        aria-label="New password"
                      />
                      <p>The rules for creating a new password are it must:</p>
                      <ul>
                        <li>be between 14 and 32 characters</li>
                        <li>
                          contain at least three out of four of the following
                          options:
                        </li>
                      </ul>
                      <ul className="secondary-list">
                        <li>- contain at least 1 numeric value</li>
                        <li>- contain at least 1 lower case character</li>
                        <li>- contain at least 1 upper case character</li>
                        <li>- contain at least 1 symbol</li>
                      </ul>
                      <FWOTextInput
                        type="password"
                        placeholder="Re-enter new password"
                        label="Re-enter new password"
                        ariaLabel="Re-enter new password"
                        name="comparePassword"
                        required={true}
                        aria-label="Re-enter new password"
                      />
                      <div className="button-group is-right">
                        <Button
                          disabled={isSubmitting || !dirty || !isValid}
                          loading={isSubmitting}
                          type="submit"
                          content="Submit"
                          className="is-primary"
                        />
                        <Button
                          as={Link}
                          to={PATHS.Profile}
                          type="button"
                          content="Cancel"
                          className="is-secondary"
                        />
                      </div>
                    </Form>
                  )}
                </Formik>
              </main>
              <aside className="sidebar-first"></aside>
              <aside className="sidebar-second"></aside>
            </div>
          </section>
        </div>
      </Suspense>
    </Protected>
  )
})

export default ResetPassword
